import { ApolloCache } from '@apollo/client';
import {
  AiDraftType,
  useAiDraftApproveMutation,
  useAiDraftRejectMutation,
  useAiDraftUpdateMutation,
} from 'lib-knowledge-management-common/graphql';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { removeFromQueue } from '../utils/draftQueueCache';
import { Suggestion } from '../utils/toSuggestion';

/** Which write is in flight, so only the button that started it says so. */
export type PendingAction = 'discard' | 'publish' | 'approve';

export type UseSuggestionActions = {
  /** Reject the draft. It is kept for the record but never listed again. */
  discard: () => void;
  publish: () => void;
  approve: () => void;
  improve: (feedback: string) => void;
  /**
   * Move the draft to a different collection or category. `null` clears one —
   * a category has to go when the collection it belongs to changes; omitting a
   * field leaves it as it is.
   */
  refile: (target: {
    collectionId?: string | null;
    categoryId?: string | null;
  }) => void;
  isImproving: boolean;
  /**
   * A re-file is in flight. The draft still carries its old collection until it
   * lands, so the category list is still the old collection's — picking from it
   * would write a category belonging to neither.
   */
  isRefiling: boolean;
  /**
   * The write in flight, if any. Only the button that started it should show
   * itself working; the others are merely unavailable meanwhile.
   */
  pendingAction: PendingAction | undefined;
  /** Any of the three is in flight. */
  isSaving: boolean;
  /**
   * Whether the draft can be approved at all. A `NEW_ARTICLE` draft needs both
   * a collection and a category first — the article it becomes has to live
   * somewhere, and the backend refuses it otherwise. An `ARTICLE_UPDATE` draft
   * is never blocked: it reports the filing of the article it updates, which is
   * the article's own and not the draft's to set.
   */
  canApprove: boolean;
  /**
   * Whichever half of the filing is missing, as a phrase — "a category", or
   * "a collection and a category" — or undefined when nothing is.
   */
  missingFiling: string | undefined;
  /** `missingFiling` as a full sentence, for the reviewer. */
  approveBlockedReason: string | undefined;
};

/**
 * The writes a reviewer can make against a draft.
 *
 * Approving and rejecting both take the draft out of the queue, so it is cut
 * from the cached lists and the caller told to drop its selection. Revising and
 * re-filing leave it in place: those return the whole list item, which lands in
 * the cache under the draft's own id and refreshes the preview and its row
 * together without a refetch.
 */
export function useSuggestionActions({
  suggestion,
  onRemoved,
}: {
  suggestion: Suggestion;
  onRemoved: () => void;
}): UseSuggestionActions {
  const { successNotify, errorNotify } = useNotification();
  const navigate = useNavigate();
  const { id, type, title, collectionId, categoryId, languageId } = suggestion;
  // The approve mutation backs two buttons, so its own `loading` cannot say
  // which one is working.
  const [pendingAction, setPendingAction] = useState<PendingAction>();

  // Both take the draft out of the queue — see `removeFromQueue`.
  const removesFromQueue = {
    update(cache: ApolloCache<unknown>) {
      removeFromQueue(cache, id);
    },
  };

  const [approveDraft, approveState] =
    useAiDraftApproveMutation(removesFromQueue);
  const [rejectDraft, rejectState] = useAiDraftRejectMutation(removesFromQueue);
  // Two hooks over the one mutation, so revising and re-filing each report
  // their own `loading`. Sharing a hook would have either one claim the other
  // was in flight, and both drive different controls.
  const [improveDraft, improveState] = useAiDraftUpdateMutation();
  const [refileDraft, refileState] = useAiDraftUpdateMutation();

  // Keyed on the ids rather than the resolved names: a collection beyond the
  // first 50 the lookup reads has no name here, and that is no reason to stop
  // the reviewer approving a draft the backend would accept.
  const missing =
    type === AiDraftType.ArticleUpdate
      ? []
      : [
          collectionId ? undefined : 'a collection',
          categoryId ? undefined : 'a category',
        ].filter((part): part is string => !!part);

  const canApprove = missing.length === 0;
  const missingFiling = canApprove ? undefined : missing.join(' and ');

  const runApprove = useCallback(
    (alsoPublish: boolean) => {
      setPendingAction(alsoPublish ? 'publish' : 'approve');

      approveDraft({ variables: { id, alsoPublish } })
        .then((result) => {
          // For a new draft the article it became, for an update the one it was
          // written against — the mutation answers with either.
          const articleId = result.data?.aiDraftApprove?.id;

          if (alsoPublish) ampli.knowledgeArticleSuggestionPublished();
          else ampli.knowledgeArticleSuggestionAddedToDrafts();

          successNotify({
            title: alsoPublish ? 'Article published' : 'Article saved',
            text: alsoPublish
              ? `“${title}” is live.`
              : `“${title}” is saved as a draft article.`,
            icon: 'check-circle',
          });

          // Dropping the selection first: it rewrites the current history entry
          // rather than pushing, so the queue the reviewer goes Back to is not
          // still pointing at a draft that no longer exists.
          onRemoved();

          // Nothing in this view can edit the article, so approving is the
          // reviewer saying they agree and want to carry on in the editor. With
          // no article to go to there is nothing to do but stay.
          if (articleId) {
            navigate(
              routeGenerator.articleEdit({
                articleId,
                collectionId,
                languageId,
              }),
            );
          }
        })
        .catch((error: unknown) =>
          errorNotify({ text: (error as Error)?.message }),
        )
        .finally(() => setPendingAction(undefined));
    },
    [
      approveDraft,
      id,
      title,
      collectionId,
      languageId,
      successNotify,
      errorNotify,
      onRemoved,
      navigate,
    ],
  );

  const discard = useCallback(() => {
    setPendingAction('discard');

    rejectDraft({ variables: { id } })
      .then(() => {
        ampli.knowledgeArticleSuggestionDiscarded();
        successNotify({
          title: 'Suggestion discarded',
          text: `“${title}” will not be suggested again.`,
        });
        onRemoved();
      })
      .catch((error: unknown) =>
        errorNotify({ text: (error as Error)?.message }),
      )
      .finally(() => setPendingAction(undefined));
  }, [rejectDraft, id, title, successNotify, errorNotify, onRemoved]);

  const improve = useCallback(
    (feedback: string) => {
      const trimmed = feedback.trim();
      // `feedback` is a `NonEmptyString`, so an empty box is not a request.
      if (!trimmed) return;

      improveDraft({ variables: { id, feedback: trimmed } })
        .then(() => ampli.knowledgeArticleSuggestionContentUpdated())
        .catch((error: unknown) =>
          errorNotify({ text: (error as Error)?.message }),
        );
    },
    [improveDraft, id, errorNotify],
  );

  const refile = useCallback(
    (target: { collectionId?: string | null; categoryId?: string | null }) => {
      // No notification on success: the breadcrumb and the selects show the
      // new filing themselves, and a toast for every pick is noise.
      refileDraft({ variables: { id, ...target } }).catch((error: unknown) =>
        errorNotify({ text: (error as Error)?.message }),
      );
    },
    [refileDraft, id, errorNotify],
  );

  return {
    discard,
    publish: () => runApprove(true),
    approve: () => runApprove(false),
    improve,
    refile,
    isImproving: improveState.loading,
    isRefiling: refileState.loading,
    pendingAction,
    isSaving: approveState.loading || rejectState.loading,
    canApprove,
    missingFiling,
    approveBlockedReason: missingFiling
      ? `Choose ${missingFiling} for this suggestion before it can be added or published.`
      : undefined,
  };
}
